import React, { FC, useContext, useMemo } from 'react';

// components
import NestedSpinner from '../../../../components/loading/nestedSpinner';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../../constants/aampeTheme';
// models
import { FormulaType } from '../../../../constants/formulaType';
import { PREVIEW_SVG_URLS } from '../../../../constants/messagePreviews';

// context
import { EditorContext } from '../../../../context/EditorContext';

import AddVariationMsg from './AddVariationMsg';
import GetStartedMsg from './GetStartedMsg';
import PreviewError from './PreviewError';

interface StyedProps {
    isSecondaryView?: boolean;
    isWhatsAppView?: boolean;
}
interface PhoneProps {
    backgroundImageUrl?: string;
    hasText: boolean;
    formulaHasVariant: boolean;
}

const Phone: FC<React.PropsWithChildren<PhoneProps>> = ({
    backgroundImageUrl = PREVIEW_SVG_URLS.android,
    hasText,
    formulaHasVariant,
    children,
}) => {
    const { editorDetails } = useContext(EditorContext);

    const {
        isGenerating,
        isPreviewError,
        previewError,
        isSecondaryView,
        formulaType,
        showSmallPhone,
    } = editorDetails;

    const backgroundUrl = useMemo(() => {
        if (!isSecondaryView) {
            return formulaType === FormulaType.whatsapp
                ? PREVIEW_SVG_URLS.whatsapp_ios
                : PREVIEW_SVG_URLS.ios;
        }
        return backgroundImageUrl;
    }, [formulaType, backgroundImageUrl, isSecondaryView]);

    return (
        <PhoneContainer showSmallPhone={showSmallPhone}>
            {backgroundUrl && <BackgroundImage src={backgroundUrl} />}
            <MessagesContainer
                isSecondaryView={isSecondaryView}
                isWhatsAppView={formulaType === FormulaType.whatsapp}
            >
                {isGenerating && <NestedSpinner />}
                {!isGenerating && isPreviewError && (
                    <PreviewError message={previewError} />
                )}
                {!isGenerating && !isPreviewError && !hasText && (
                    <GetStartedMsg />
                )}
                {!isGenerating &&
                    !isPreviewError &&
                    hasText &&
                    !formulaHasVariant && <AddVariationMsg />}
                {!isGenerating && !isPreviewError && hasText && children}
            </MessagesContainer>
        </PhoneContainer>
    );
};

const PhoneContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'showSmallPhone',
})<{ showSmallPhone?: boolean }>(({ showSmallPhone }) => ({
    height: showSmallPhone ? '510px' : '648px',
    width: showSmallPhone ? '240px' : '307px',
    zIndex: 1,
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    position: 'relative',
}));

const BackgroundImage = styled.img({
    height: '100%',
    width: '100%',
    zIndex: 2,
    position: 'absolute',
    borderRadius: '36px',
});

const MessagesContainer = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        prop !== 'isSecondaryView' && prop !== 'isWhatsAppView', // Exclude props from being forwarded to the DOM
})<StyedProps>(({ isSecondaryView, isWhatsAppView }) => ({
    height: '78%',
    width: '100%',
    zIndex: 2,
    position: 'absolute',
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    marginTop: '20%',
    gap: aampeTheme.spacing(1),
    overflowY: 'auto',
    overflowX: 'hidden',
    opacity: isSecondaryView ? 1 : 0.9,
    paddingLeft:
        isSecondaryView || isWhatsAppView
            ? aampeTheme.spacing(0)
            : aampeTheme.spacing(1),
}));

export default Phone;
